import { supabase } from '@/lib/supabase';
import { LEAD_PRICE } from '@/data/constants';

/**
 * ───────────────────────────────────────────────────────────────────────────
 * $29 SINGLE LEAD CREDIT — client side of the one-off purchase.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * ONE RULE, IDENTICAL TO MEMBERSHIPS:
 *
 *   Basic  $99  -> 5 credits  ─┐
 *   Pro    $199 -> 10 credits  ├─ land in the balance, then the contractor
 *   Premium$499 -> 20 credits  │  spends them with Unlock Lead
 *   Single $29  -> 1 credit   ─┘
 *
 * BUYING IS NOT SPENDING. The $29 purchase used to grant a credit and consume
 * it on the spot, on whichever project happened to be open. That made the
 * one-off behave unlike every membership and took the choice away from the
 * customer. Now it simply tops the balance up by one.
 *
 * Every call below is a thin wrapper over the `single-lead-purchase` edge
 * function. Nothing here is trusted: the buyer is resolved from the bearer
 * token server-side, and the grant happens in ONE transaction keyed by the
 * payment intent id, so a double tap, a second tab or a replayed request can
 * never double charge or double credit.
 */


export const SINGLE_LEAD_PRICE = LEAD_PRICE;

/** Publishable key + connected account for Stripe Elements. */
export const STRIPE_PUBLISHABLE_KEY = 'pk_live_9eBOACiG83hSt0n4y7Kg9Exo';
export const STRIPE_ACCOUNT_ID = 'acct_1UDYNuB5WwGm6PiX';

export interface LeadPreflight {
  ok: boolean;
  found: boolean;
  soldOut: boolean;
  alreadyOwned: boolean;
  slotsTaken: number;
  slotsMax: number;
  extraCredits: number;
  /** An unfinished payment for this same lead that can be resumed. */
  pendingPaymentIntentId: string | null;
  error?: string;
}

export interface LeadIntent {
  clientSecret: string | null;
  paymentIntentId: string | null;
  soldOut: boolean;
  alreadyOwned: boolean;
  error?: string;
  message?: string;
}

export interface LeadSettlement {
  ok: boolean;
  /**
   * TRUE ONLY for legacy orders settled under the retired auto-consume rule.
   * A purchase made today NEVER unlocks anything by itself — the credit is
   * banked and the contractor spends it with Unlock Lead.
   */
  unlocked: boolean;
  /** The payment added a credit to the balance (false on an idempotent replay). */
  credited: boolean;
  /** How many credits this payment put in the balance. Always 1 or 0. */
  creditsGranted: number;
  reason: string;
  purchaseId: string | null;
  /** One-off credits bought at $29 each. */
  extraCredits: number;
  /** Credits included with an active membership. */
  planCredits: number;
  /** Everything the contractor can spend right now. */
  balance: number;
  slotsTaken: number;
  slotsMax: number;
  error?: string;
}


/** Coarse device label stored with the order, so mobile issues are traceable. */
export const deviceLabel = (): string => {
  if (typeof navigator === 'undefined') return 'server';
  const ua = navigator.userAgent || '';
  const mobile = /iPhone|iPad|iPod|Android/i.test(ua);
  const browser = /CriOS/i.test(ua)
    ? 'chrome-ios'
    : /FxiOS/i.test(ua)
    ? 'firefox-ios'
    : /Safari/i.test(ua) && !/Chrome|Chromium|Edg/i.test(ua)
    ? 'safari'
    : /Edg/i.test(ua)
    ? 'edge'
    : /Chrome|Chromium/i.test(ua)
    ? 'chrome'
    : 'other';
  return `${mobile ? 'mobile' : 'desktop'}/${browser}`;
};

/** True for touch-first viewports — used to avoid popup-blocked new tabs. */
export const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined') return false;
  const narrow = window.matchMedia?.('(max-width: 767px)')?.matches ?? window.innerWidth < 768;
  const touch = 'ontouchstart' in window || (navigator.maxTouchPoints || 0) > 0;
  return narrow || touch;
};

/**
 * RESUME KEY. The selected lead + payment intent are remembered locally so a
 * customer who is interrupted (bank app switch, backgrounded Safari tab, dead
 * battery, accidental reload) comes back to the SAME lead and we settle the
 * same payment instead of asking them to find the lead again or pay twice.
 */
const RESUME_KEY = 'gg_single_lead_intent';

export interface ResumeTicket {
  listingId: string;
  slug: string;
  paymentIntentId: string;
  at: number;
}

export const rememberIntent = (ticket: Omit<ResumeTicket, 'at'>) => {
  try {
    localStorage.setItem(RESUME_KEY, JSON.stringify({ ...ticket, at: Date.now() }));
  } catch {
    /* private mode — resume is a convenience, never a requirement */
  }
};

export const readIntent = (listingId?: string): ResumeTicket | null => {
  try {
    const raw = localStorage.getItem(RESUME_KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as ResumeTicket;
    if (!t?.paymentIntentId) return null;
    // Stale tickets (> 6h) are ignored so we never settle something ancient.
    if (Date.now() - (t.at || 0) > 6 * 60 * 60 * 1000) return null;
    if (listingId && t.listingId !== listingId) return null;
    return t;
  } catch {
    return null;
  }
};

export const clearIntent = () => {
  try {
    localStorage.removeItem(RESUME_KEY);
  } catch {
    /* ignore */
  }
};

const call = async (body: Record<string, unknown>) => {
  const { data, error } = await supabase.functions.invoke('single-lead-purchase', { body });
  if (error && !data) return { error: 'network' } as Record<string, unknown>;
  return (data || { error: 'error' }) as Record<string, unknown>;
};

/** Re-check role, capacity and entitlement BEFORE showing a card form. */
export const preflightLead = async (listingId: string): Promise<LeadPreflight> => {
  const d = await call({ action: 'preflight', listingId });
  return {
    ok: d.ok === true,
    found: d.found !== false,
    soldOut: d.soldOut === true,
    alreadyOwned: d.alreadyOwned === true,
    slotsTaken: Number(d.slotsTaken || 0),
    slotsMax: Number(d.slotsMax || 6),
    extraCredits: Number(d.extraCredits || 0),
    pendingPaymentIntentId: (d.pendingPaymentIntentId as string) || null,
    error: (d.error as string) || undefined,
  };
};

/** Create (or reuse) the $29 PaymentIntent for a specific lead. */
export const createLeadIntent = async (listingId: string, language: string): Promise<LeadIntent> => {
  const d = await call({ action: 'intent', listingId, language, device: deviceLabel() });
  return {
    clientSecret: (d.clientSecret as string) || null,
    paymentIntentId: (d.paymentIntentId as string) || null,
    soldOut: d.soldOut === true,
    alreadyOwned: d.alreadyOwned === true,
    error: (d.error as string) || undefined,
    message: (d.message as string) || undefined,
  };
};

/**
 * Settle a succeeded payment.
 *
 * BUSINESS RULE: this ADDS ONE CREDIT TO THE BALANCE. It does not spend it and
 * does not unlock anything — exactly like a membership renewal dropping 5, 10
 * or 20 credits in. The contractor decides when and where to spend it by
 * pressing Unlock Lead.
 *
 * SAFE TO CALL REPEATEDLY: the database keys the grant on the payment intent
 * id under a row lock, so retries, double taps and a second tab all return the
 * same balance and never issue a second credit.
 */
export const settleLeadPayment = async (
  paymentIntentId: string,
  listingId?: string,
  language = 'en'
): Promise<LeadSettlement> => {
  const d = await call({ action: 'settle', paymentIntentId, listingId, language });
  const extra = Number(d.extraCredits || 0);
  const plan = Number(d.planCredits || 0);
  return {
    ok: d.ok === true,
    unlocked: d.unlocked === true,
    credited: d.credited === true,
    creditsGranted: Number(d.creditsGranted || 0),
    reason: (d.reason as string) || '',
    purchaseId: (d.purchaseId as string) || null,
    extraCredits: extra,
    planCredits: plan,
    balance: Number(d.balance ?? extra + plan),
    slotsTaken: Number(d.slotsTaken || 0),
    slotsMax: Number(d.slotsMax || 6),
    error: (d.error as string) || undefined,
  };
};
